import React, { useState, useRef, useEffect, useCallback } from 'react';
import Dialog from 'src/componentes/dialog';
import http from 'src/services/httpService';
import errorHandler from 'src/utils/error-handler';
import { Table } from 'reactstrap';
import LucideIcon from 'src/componentes/lucide-icon';
import LoadingContainer from 'src/componentes/loading-container';
import { FormattedMessage, injectIntl } from 'react-intl';
import Button from 'src/componentes/button';
import { formatNumber } from 'src/utils/number';
import { capitalizeFirstLetter } from 'src/utils/string';
import ContentManager from 'src/componentes/content-manager';
import AcaoEdit from 'src/paginas/acao/edit';
import { parse, format } from 'date-fns';
import getEditUrl from 'src/utils/item/getEditUrl';
import { TIPO_ORIGEM_DETALHAMENTO_INVESTIMENTO_CUSTEIO } from 'src/utils/constants';

function DetalhamentoInvestimentoCusteioModal(props) {
    const dialogRef = useRef(null);
    const [isLoading, setIsLoading] = useState(true);
    const [origensValor, setOrigensValor] = useState([]);
    const [expandedOrigensValor, setExpandedOrigensValor] = useState(new Set());
    const [detalhes, setDetalhes] = useState({});
    const [isLoadingDetalhes, setIsLoadingDetalhes] = useState({});

    const carregarOrigensValor = useCallback(() => {
        const { projetoId, acaoId } = props;

        setIsLoading(true);

        http
            .post('/Projeto/ObterDetalhamentoInvestimentoCusteio', {
                projetoId,
                acaoId,
            })
            .then((response) => {
                setOrigensValor(response.data.origensValor || []);
                setIsLoading(false);
            })
            .catch((error) => {
                errorHandler(error);
                setIsLoading(false);
            });
    }, [props.projetoId, props.acaoId]);

    useEffect(() => {
        carregarOrigensValor();
    }, [carregarOrigensValor]);

    const handleExpandir = useCallback((tipoId) => {
        setExpandedOrigensValor((prevExpanded) => {
            const newExpandedOrigensValor = new Set(prevExpanded);

            if (newExpandedOrigensValor.has(tipoId)) {
                // Colapsar
                newExpandedOrigensValor.delete(tipoId);
                setDetalhes((prevDetalhes) => {
                    const newDetalhes = { ...prevDetalhes };
                    delete newDetalhes[tipoId];
                    return newDetalhes;
                });
                setIsLoadingDetalhes((prevIsLoading) => {
                    const newIsLoadingDetalhes = { ...prevIsLoading };
                    delete newIsLoadingDetalhes[tipoId];
                    return newIsLoadingDetalhes;
                });
                return newExpandedOrigensValor;
            }

            // Expandir
            newExpandedOrigensValor.add(tipoId);
            setIsLoadingDetalhes((prevIsLoading) => ({ ...prevIsLoading, [tipoId]: true }));

            const { projetoId, acaoId } = props;

            http
                .post('/Projeto/ObterDetalhesOrigemValor', {
                    tipoId,
                    projetoId,
                    acaoId,
                })
                .then((response) => {
                    setDetalhes((prevDetalhes) => ({
                        ...prevDetalhes,
                        [tipoId]: response.data.itens || [],
                    }));
                    setIsLoadingDetalhes((prevIsLoading) => ({
                        ...prevIsLoading,
                        [tipoId]: false,
                    }));
                })
                .catch((error) => {
                    errorHandler(error);
                    setIsLoadingDetalhes((prevIsLoading) => ({
                        ...prevIsLoading,
                        [tipoId]: false,
                    }));
                });

            return newExpandedOrigensValor;
        });
    }, [props.projetoId, props.acaoId]);

    const handleClose = useCallback(() => {
        dialogRef.current?.close();

        setTimeout(() => {
            props.remove?.();
        }, 300);
    }, [props.remove]);

    const handleItemNomeClick = useCallback(
        (origemValor, item) => {
            if (!origemValor || !item) return;

            const tipoOrigemId = origemValor?.tipo?.id;
            const { projetoId, acaoId } = props;

            if (tipoOrigemId === TIPO_ORIGEM_DETALHAMENTO_INVESTIMENTO_CUSTEIO.SUBPROJETOS) {
                const url = `${getEditUrl(4)}?id=${item.id}`;
                window.open(url, '_blank');
                return;
            }

            if (tipoOrigemId === TIPO_ORIGEM_DETALHAMENTO_INVESTIMENTO_CUSTEIO.ACOES) {
                ContentManager.addContent(
                    <AcaoEdit
                        defaultActiveKey={0}
                        showExport
                        handleClose={() => {}}
                        item={{
                            id: item.id,
                            descricao: item.nome,
                            codigo: item.codigo,
                        }}
                        parameters={{ exibirLookups: true }}
                    />
                );
                return;
            }

            if (tipoOrigemId === TIPO_ORIGEM_DETALHAMENTO_INVESTIMENTO_CUSTEIO.PLANO_CONTAS) {
                const contaId = item.id;
                const origemPlanoContas = origensValor.find(
                    (o) => o.tipo?.id === TIPO_ORIGEM_DETALHAMENTO_INVESTIMENTO_CUSTEIO.PLANO_CONTAS
                );
                const dataInicio = format(
                    parse(origemPlanoContas.dataInicio, 'yyyy-MM-dd', new Date()),
                    'yyyy-MM-dd'
                );
                const dataTermino = format(
                    parse(origemPlanoContas.dataTermino, 'yyyy-MM-dd', new Date()),
                    'yyyy-MM-dd'
                );

                let query =
                    projetoId != null
                        ? `projetoId=${projetoId}&contaId=${contaId}`
                        : acaoId != null
                            ? `acaoId=${acaoId}&contaId=${contaId}`
                            : `contaId=${contaId}`;
                query += `&dataInicio=${dataInicio}&dataTermino=${dataTermino}`;
                window.open(`/acompanhar/valor-planocontas-projeto?${query}`, '_blank');
            }
        },
        [props.projetoId, props.acaoId, origensValor]
    );

    const renderColunasHeader = () => {
        const { resources, intl } = props;

        const headerStyle = {
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            fontWeight: 'normal',
        };

        return (
            <tr>
                <th style={{ minWidth: 200, ...headerStyle }}>
                    {capitalizeFirstLetter(intl.formatMessage({ id: 'categoria' }))} / {capitalizeFirstLetter(intl.formatMessage({ id: 'item' }))}
                </th>
                <th className="text-right" style={headerStyle}>
                    {capitalizeFirstLetter(intl.formatMessage({ id: 'investimentoPrevisto' }, { investimento: resources?.investimento }))}
                </th>
                <th className="text-right" style={headerStyle}>
                    {capitalizeFirstLetter(intl.formatMessage({ id: 'investimentoPrevistoRetificado' }, { investimento: resources?.investimento }))}
                </th>
                <th className="text-right" style={headerStyle}>
                    {capitalizeFirstLetter(intl.formatMessage({ id: 'investimentoRealizado' }, { investimento: resources?.investimento }))}
                </th>
                <th className="text-right" style={headerStyle}>
                    {capitalizeFirstLetter(intl.formatMessage({ id: 'custeioPrevisto' }, { custeio: resources?.custeio }))}
                </th>
                <th className="text-right" style={headerStyle}>
                    {capitalizeFirstLetter(intl.formatMessage({ id: 'custeioPrevistoRetificado' }, { custeio: resources?.custeio }))}
                </th>
                <th className="text-right" style={headerStyle}>
                    {capitalizeFirstLetter(intl.formatMessage({ id: 'custeioRealizado' }, { custeio: resources?.custeio }))}
                </th>
            </tr>
        );
    };

    const renderOrigemValorRow = (origemValor) => {
        const { resources } = props;
        const tipoId = origemValor.tipo.id;
        const isExpanded = expandedOrigensValor.has(tipoId);
        const temInvestimentoRetificado = origemValor.investimentoPrevistoRetificado != null;
        const temCusteioRetificado = origemValor.custeioPrevistoRetificado != null;

        return (
            <tr
                key={tipoId}
                style={{ fontWeight: 'bold', cursor: 'pointer', borderTop: '1px solid #dee2e6', borderBottom: '1px solid #dee2e6', backgroundColor: '#f8f9fa' }}
                onClick={() => handleExpandir(tipoId)}
            >
                <td>
                    <LucideIcon icon={isExpanded ? 'chevron-up' : 'chevron-down'} className="mr-2" />
                    {capitalizeFirstLetter(resources?.[origemValor.tipo.resource])}
                </td>
                <td className="text-right">{origemValor.investimentoPrevisto != null ? formatNumber(origemValor.investimentoPrevisto, 2) : '-'}</td>
                <td className="text-right">
                    {temInvestimentoRetificado ? formatNumber(origemValor.investimentoPrevistoRetificado, 2) : '-'}
                </td>
                <td className="text-right">{origemValor.investimentoRealizado != null ? formatNumber(origemValor.investimentoRealizado, 2) : '-'}</td>
                <td className="text-right">{origemValor.custeioPrevisto != null ? formatNumber(origemValor.custeioPrevisto, 2) : '-'}</td>
                <td className="text-right">
                    {temCusteioRetificado ? formatNumber(origemValor.custeioPrevistoRetificado, 2) : '-'}
                </td>
                <td className="text-right">{origemValor.custeioRealizado != null ? formatNumber(origemValor.custeioRealizado, 2) : '-'}</td>
            </tr>
        );
    };

    const renderDetalhesRows = (origemValor) => {
        const tipoId = origemValor.tipo.id;

        if (!expandedOrigensValor.has(tipoId)) return null;

        const origemValorDetalhes = detalhes[tipoId] || [];
        const origemValorIsLoading = isLoadingDetalhes[tipoId];

        if (origemValorIsLoading) {
            return (
                <tr key={`${tipoId}-loading`}>
                    <td colSpan={7} className="text-center" style={{ padding: 15 }}>
                        <FormattedMessage id="carregando" />
                    </td>
                </tr>
            );
        }

        if (!origemValorDetalhes || origemValorDetalhes.length === 0) {
            return (
                <tr key={`${tipoId}-empty`}>
                    <td colSpan={7} className="text-center" style={{ padding: 10 }}>
                        <FormattedMessage id="label.nenhumRegistroEncontrado" />
                    </td>
                </tr>
            );
        }

        return origemValorDetalhes.map((item, index) => {
            const temInvestimentoRetificado = item.investimentoPrevistoRetificado != null;
            const temCusteioRetificado = item.custeioPrevistoRetificado != null;

            return (
                <tr key={`${tipoId}-${item.id || index}`} style={{ borderTop: '1px solid #dee2e6', borderBottom: '1px solid #dee2e6' }}>
                    <td style={{ paddingLeft: 30 }}>
                        <a
                            onClick={() => handleItemNomeClick(origemValor, item)}
                            className="text-primary cursor-pointer"
                            tabIndex="-1"
                        >
                            {item.nome}
                        </a>
                    </td>
                    <td className="text-right">{item.investimentoPrevisto != null ? formatNumber(item.investimentoPrevisto, 2) : '-'}</td>
                    <td className="text-right">
                        {temInvestimentoRetificado ? formatNumber(item.investimentoPrevistoRetificado, 2) : '-'}
                    </td>
                    <td className="text-right">{item.investimentoRealizado != null ? formatNumber(item.investimentoRealizado, 2) : '-'}</td>
                    <td className="text-right">{item.custeioPrevisto != null ? formatNumber(item.custeioPrevisto, 2) : '-'}</td>
                    <td className="text-right">
                        {temCusteioRetificado ? formatNumber(item.custeioPrevistoRetificado, 2) : '-'}
                    </td>
                    <td className="text-right">{item.custeioRealizado != null ? formatNumber(item.custeioRealizado, 2) : '-'}</td>
                </tr>
            );
        });
    };

    const somarValor = (origensValor, propriedade) => {
        return origensValor.reduce((soma, origemValor) => {
            return soma + Number(origemValor[propriedade] || 0);
        }, 0);
    };

    const calcularTotais = () => {
        const hasValoresRetificados = origensValor.some(
            (o) => o.investimentoPrevistoRetificado != null || o.custeioPrevistoRetificado != null
        );

        const totais = {
            investimentoPrevisto: somarValor(origensValor, 'investimentoPrevisto'),
            investimentoPrevistoRetificado: somarValor(origensValor, 'investimentoPrevistoRetificado'),
            investimentoRealizado: somarValor(origensValor, 'investimentoRealizado'),
            custeioPrevisto: somarValor(origensValor, 'custeioPrevisto'),
            custeioPrevistoRetificado: somarValor(origensValor, 'custeioPrevistoRetificado'),
            custeioRealizado: somarValor(origensValor, 'custeioRealizado'),
        };

        return { totais, hasValoresRetificados };
    };

    const renderTotalRow = () => {
        const { totais, hasValoresRetificados } = calcularTotais();

        return (
            <tr style={{ fontWeight: 'bold', borderTop: '2px solid #dee2e6', borderBottom: '1px solid #dee2e6' }}>
                <td>
                    <FormattedMessage id="total" />
                </td>
                <td className="text-right">{formatNumber(totais.investimentoPrevisto, 2)}</td>
                <td className="text-right">
                    {hasValoresRetificados ? formatNumber(totais.investimentoPrevistoRetificado, 2) : '-'}
                </td>
                <td className="text-right">{formatNumber(totais.investimentoRealizado, 2)}</td>
                <td className="text-right">{formatNumber(totais.custeioPrevisto, 2)}</td>
                <td className="text-right">
                    {hasValoresRetificados ? formatNumber(totais.custeioPrevistoRetificado, 2) : '-'}
                </td>
                <td className="text-right">{formatNumber(totais.custeioRealizado, 2)}</td>
            </tr>
        );
    };

    const { intl } = props;

    return (
        <Dialog
            ref={dialogRef}
            width="68vw"
            height="36.8rem"
            styleHeader={{ paddingLeft: '25px', paddingRight: '25px', paddingTop: '20px' }}
            styleFooter={{ paddingLeft: '25px', paddingRight: '25px', paddingBottom: '20px' }}
            customTitle={<span style={{ color: '#000', fontWeight: 'bold' }}><FormattedMessage id="label.detalhamento" /></span>}
            actions={[
                <Button
                    key="fechar"
                    onClick={handleClose}
                    style={{ backgroundColor: '#fff', color: '#000', border: '1px solid #dee2e6' }}
                >
                    <FormattedMessage id="label.fechar" />
                </Button>
            ]}
            onRequestClose={handleClose}
        >
            <div style={{ paddingLeft: '25px', paddingRight: '25px' }}>
                <div
                    className="d-flex align-items-center mb-3 p-2 bg-primary-light rounded-lg"
                    style={{ color: '#1D7789', flexShrink: 0 }}
                >
                    <LucideIcon icon="information-outline" className="mr-2" />
                    <span>
                        {intl.formatMessage({ id: 'infoModalDetalheOrcamento' })}
                    </span>
                </div>
                <div style={{ overflow: 'auto', height: '25rem' }}>
                    <LoadingContainer isLoading={isLoading}>
                        {origensValor.length === 0 && !isLoading ? (
                            <div className="text-center p-4">
                                <FormattedMessage id="valorDigitadoImportado" />
                            </div>
                        ) : (
                            <Table size="sm" responsive style={{ marginTop: 10 }}>
                                <thead>{renderColunasHeader()}</thead>
                                <tbody>
                                    {origensValor.map((origemValor) => (
                                        <React.Fragment key={origemValor.tipo.id}>
                                            {renderOrigemValorRow(origemValor)}
                                            {renderDetalhesRows(origemValor)}
                                        </React.Fragment>
                                    ))}
                                    {origensValor.length > 0 && renderTotalRow()}
                                </tbody>
                            </Table>
                        )}
                    </LoadingContainer>
                </div>
            </div>
        </Dialog>
    );
}

export default injectIntl(DetalhamentoInvestimentoCusteioModal);
